import { useApprovalRequestConfirmationCommentUpdate } from "@/queries/approval/use-approval-requests.query.ts";
import { useOfferConfirmationCommentUpdate } from "@/queries/recruit/use-offer.query.ts";
import { useHiringPlanConfirmationCommentUpdate } from "@/queries/recruit/use-hiring-plan.query.ts";
import { useTimeOffConfirmationCommentUpdate } from "@/queries/use-time-off-requests.query.ts";
import {
  ApiApprovalRequestConfirmation,
  ApiApprovalRequestDetails,
  ApiApprovalRequestType,
} from "@/types/approvals/approval.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Textarea,
  ExpandableText,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface ApprovalConfirmationCommentProps {
  confirmation: ApiApprovalRequestConfirmation;
  request: ApiApprovalRequestDetails;
  isAddingComment?: boolean;
  onEditEnd?: () => void;
  onCommentSaved?: (comment: string | null) => void;
}

export function ApprovalConfirmationComment({
  confirmation,
  request,
  isAddingComment = false,
  onEditEnd,
  onCommentSaved,
}: ApprovalConfirmationCommentProps) {
  const requestId = request.id;
  const requestType = request.type;
  const { t } = useTranslation();
  const [editIsOn, setEditIsOn] = useState(isAddingComment);
  const [editValue, setEditValue] = useState(isAddingComment ? (confirmation.comment ?? "") : "");
  const [menuOpen, setMenuOpen] = useState(false);

  const updateTimeOffComment = useTimeOffConfirmationCommentUpdate();
  const updateOfferComment = useOfferConfirmationCommentUpdate();
  const updateHiringPlanComment = useHiringPlanConfirmationCommentUpdate();
  const updateApprovalRequestComment = useApprovalRequestConfirmationCommentUpdate();
  const isSaving =
    updateTimeOffComment.isPending ||
    updateOfferComment.isPending ||
    updateHiringPlanComment.isPending ||
    updateApprovalRequestComment.isPending;

  const [syncedComment, setSyncedComment] = useState(confirmation.comment);
  const [syncedIsAddingComment, setSyncedIsAddingComment] = useState(isAddingComment);

  if (confirmation.comment !== syncedComment || isAddingComment !== syncedIsAddingComment) {
    setSyncedComment(confirmation.comment);
    setSyncedIsAddingComment(isAddingComment);
    if (isAddingComment) {
      setEditValue(confirmation.comment ?? "");
      setEditIsOn(true);
    }
  }

  const handleEditStart = () => {
    setEditValue(confirmation.comment ?? "");
    setEditIsOn(true);
  };

  const handleEditEnd = () => {
    setEditIsOn(false);
    onEditEnd?.();
  };

  const handleSave = async () => {
    const payload = { comment: editValue || null };
    switch (requestType) {
      case ApiApprovalRequestType.offer:
        await updateOfferComment.mutateAsync({ id: requestId, payload });
        break;
      case ApiApprovalRequestType.hiring_plan:
        await updateHiringPlanComment.mutateAsync({ id: requestId, payload });
        break;
      case ApiApprovalRequestType.time_off:
        await updateTimeOffComment.mutateAsync({ id: requestId, payload });
        break;
      case ApiApprovalRequestType.workflow:
        await updateApprovalRequestComment.mutateAsync({ id: requestId, payload });
    }
    onCommentSaved?.(payload.comment);
    handleEditEnd();
  };

  const handleDelete = async () => {
    const payload = { comment: null };
    switch (requestType) {
      case ApiApprovalRequestType.offer:
        await updateOfferComment.mutateAsync({ id: requestId, payload });
        break;
      case ApiApprovalRequestType.hiring_plan:
        await updateHiringPlanComment.mutateAsync({ id: requestId, payload });
        break;
      case ApiApprovalRequestType.time_off:
        await updateTimeOffComment.mutateAsync({ id: requestId, payload });
        break;
      case ApiApprovalRequestType.workflow:
        await updateApprovalRequestComment.mutateAsync({ id: requestId, payload });
    }
  };

  return (
    <>
      {editIsOn ? (
        <Stack gap={"sm"} className={"pl-12"}>
          <Textarea
            value={editValue}
            label={t("common:comment")}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleEditEnd();
            }}
            autoFocus
          />
          <Stack direction={"horizontal"} gap={"sm"} justify={"end"}>
            <Button size={"sm"} onClick={handleEditEnd} variant={"secondary"} disabled={isSaving}>
              {t("common:cancel")}
            </Button>
            <Button size={"sm"} onClick={handleSave} isLoading={isSaving}>
              {t("common:save")}
            </Button>
          </Stack>
        </Stack>
      ) : confirmation.comment ? (
        <Box variant={"border"} className={"group flex-1 ml-12"}>
          <Stack direction={"horizontal"} gap={"xs"} justify={"between"} items={"start"}>
            <ExpandableText maxLines={2} className={"break-word whitespace-pre-wrap"}>
              {confirmation.comment}
            </ExpandableText>
            {confirmation.acl.can_edit_comment && (
              <div
                className={twMerge(
                  "transition-opacity opacity-0 group-hover:opacity-100 focus-within:opacity-100",
                  menuOpen && "opacity-100"
                )}
              >
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuItem
                    onClick={isSaving ? undefined : handleEditStart}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                    title={t("common:edit")}
                  />
                  <DropdownMenuItem
                    onClick={isSaving ? undefined : handleDelete}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                    variant={"menuDanger"}
                    title={t("common:delete")}
                  />
                </DropdownMenu>
              </div>
            )}
          </Stack>
        </Box>
      ) : undefined}
    </>
  );
}
